"use client";

import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  APP_DEFAULT_TIME_ZONE,
  formatDateInputLong,
  formatTimeInput12Hour,
  joinDateTimeInput,
  splitDateTimeInput,
  toZonedDateInput,
} from "@/lib/dates/zoned-input";
import { cn } from "@/lib/utils";

/**
 * A date (and optionally time) field that renders a calendar instead of the
 * browser's native picker.
 *
 * A native `<input type="date">` formats its visible value in the **browser's**
 * locale, with no attribute to override it — so an en-US browser showed
 * 5 September 2026 as `09/05/2026`, which is indistinguishable from 9 May. That
 * is not a styling preference; queens were misreading event dates. This control
 * renders the value itself ("05 Sep 2026"), so the format no longer depends on
 * the viewer's machine.
 *
 * The `value`/`onChange` contract is the same wall-clock string a native input
 * uses — `"YYYY-MM-DD"` or `"YYYY-MM-DDTHH:mm"` — so it is a drop-in
 * replacement. It stays a plain wall clock on purpose: converting to and from
 * an instant belongs in one place (`src/lib/dates/zoned-input.ts`) against an
 * explicit timezone, not spread across input components.
 */
type DateTimeFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** Include a time input beside the calendar. */
  withTime?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  /** Earliest selectable day, as `"YYYY-MM-DD"`. */
  min?: string;
  /** Latest selectable day, as `"YYYY-MM-DD"`. */
  max?: string;
  className?: string;
  "aria-label"?: string;
  /**
   * Forwarded to the time input. The date half is a button, which native form
   * validation cannot check — a caller that needs a date to be mandatory has to
   * assert it on submit.
   */
  required?: boolean;
  /**
   * Zone used only to pick today's date when someone sets a time before
   * choosing a day. The stored value stays a bare wall clock either way.
   */
  timeZone?: string;
};

/** Parses `"YYYY-MM-DD"` into the local-noon Date react-day-picker wants. */
function toCalendarDate(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;

  // Noon, so a day never shifts when react-day-picker does its own local-time
  // arithmetic. Only the y/m/d are ever read back out.
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
  );
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** The inverse: a calendar selection back to a `"YYYY-MM-DD"` key. */
function fromCalendarDate(date: Date | undefined): string {
  if (!date) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function DateTimeField({
  value,
  onChange,
  withTime = false,
  onBlur,
  disabled,
  id,
  name,
  placeholder,
  min,
  max,
  className,
  "aria-label": ariaLabel,
  required,
  timeZone = APP_DEFAULT_TIME_ZONE,
}: DateTimeFieldProps) {
  const [open, setOpen] = React.useState(false);
  const { date, time } = splitDateTimeInput(value);

  const commit = (nextDate: string, nextTime: string) => {
    if (!nextDate) {
      onChange("");
      return;
    }
    onChange(withTime ? joinDateTimeInput(nextDate, nextTime) : nextDate);
  };

  const label = date
    ? withTime
      ? `${formatDateInputLong(date)}${time ? `, ${formatTimeInput12Hour(time)}` : ""}`
      : formatDateInputLong(date)
    : "";

  const minDate = min ? toCalendarDate(min) : undefined;
  const maxDate = max ? toCalendarDate(max) : undefined;

  return (
    <div className={cn("flex gap-2", className)}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) onBlur?.();
        }}
      >
        {/* `nativeButton` because the trigger below really is a <button>;
            Base UI warns otherwise. */}
        <PopoverTrigger asChild nativeButton>
          <Button
            type="button"
            variant="outline"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
              "h-10 min-w-0 flex-1 justify-start gap-2 px-3 font-normal",
              !date && "text-text-tertiary",
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" />
            <span className="truncate">
              {label || placeholder || "Select a date"}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            autoFocus
            captionLayout="dropdown"
            selected={toCalendarDate(date)}
            defaultMonth={toCalendarDate(date)}
            disabled={
              minDate || maxDate
                ? { before: minDate as Date, after: maxDate as Date }
                : undefined
            }
            onSelect={(selected) => {
              const nextDate = fromCalendarDate(selected);
              if (!nextDate) return;
              // Give a fresh date-time a sane clock rather than 00:00, which
              // reads as "not set yet" to anyone scanning the form.
              commit(nextDate, withTime ? time || "09:00" : "");
              if (!withTime) setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {withTime ? (
        <Input
          type="time"
          name={name}
          value={time}
          required={required}
          disabled={disabled}
          aria-label={ariaLabel ? `${ariaLabel} time` : "Time"}
          onBlur={onBlur}
          onChange={(event) => {
            const nextTime = event.target.value;
            // A time with no date yet has nothing to attach to; default to
            // today so the field cannot hold a half-value.
            commit(date || toZonedDateInput(new Date(), timeZone), nextTime);
          }}
          className="h-10 w-[7.5rem] shrink-0"
        />
      ) : null}

      {name && !withTime ? (
        <input type="hidden" name={name} value={value} />
      ) : null}
    </div>
  );
}
